import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '@app/env-service';

// INTERFACES
import { PaginationInterface } from '@app/interfaces/pagination.model';
import { PoShipmentInterface } from '@app/interfaces/purchase-order-shipment.model';

@Injectable({
  providedIn: 'root',
})
export class PurchaseOrderShipmentService {
  baseUrl :string = `${environment.urlV2}/purchase-orders/shipments`;

  private readonly _shipments = new BehaviorSubject<PoShipmentInterface[]>(
    <PoShipmentInterface[]>[],
  );
  private readonly _shipment = new BehaviorSubject<PoShipmentInterface>(
    <PoShipmentInterface>null,
  );
  private readonly _loadingPopup = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _gettingShipment = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _submittingForm = new BehaviorSubject<boolean>(<boolean>false);

  private readonly _pagination = new BehaviorSubject<PaginationInterface>(
    <PaginationInterface>{},
  );

  private readonly _deleting = new BehaviorSubject<boolean>(<boolean>false);

  shipments$ = this._shipments.asObservable();
  shipment$ = this._shipment.asObservable();
  pagination$ = this._pagination.asObservable();
  submittingForm$ = this._submittingForm.asObservable();
  loading$ = this._loading.asObservable();
  gettingShipment$ = this._gettingShipment.asObservable();
  deleting$ = this._deleting.asObservable();
  loadingPopup$ = this._loadingPopup.asObservable();

  constructor(private http :HttpClient) {}

  getById(id :number, loadFromPopup :boolean = false) :Subscription {
    const loading = loadFromPopup ? this._loadingPopup : this._gettingShipment;

    loading.next(true);
    if (!loadFromPopup) {
      this._shipment.next(null);
    }

    return this.http.get(`${this.baseUrl}/${id}`, {}).subscribe(
      (response :PoShipmentInterface) => {
        loading.next(false);
        this._shipment.next(response);
      },
      (response :any) => {
        loading.next(false);
        throwError(response.error);
      },
    );
  }

  getByPurchaseOrderId(
    purchaseOrderId :number,
    params ?:{ [param :string] :number | string | string[] },
  ) :Subscription {
    this._loading.next(true);

    params = {
      ...params,
      purchaseOrderId: purchaseOrderId,
    };

    return this.http.get(`${this.baseUrl}`, { params }).subscribe(
      (response :any) => {
        this._loading.next(false);
        this._shipments.next(response.data);
        this._pagination.next(response.meta);
      },
      (response :any) => throwError(response.error),
    );
  }

  create(shipments :Partial<PoShipmentInterface>) :Observable<any> {
    this._submittingForm.next(true);

    return this.http.post(`${this.baseUrl}`, shipments).pipe(
      map((response :PoShipmentInterface) => {
        const shipmentStatus = this.getShipments();
        this._shipments.next([...shipmentStatus, response]);
        this._submittingForm.next(false);

        return response;
      }),
      catchError((response) => {
        this._submittingForm.next(false);

        return throwError(response.error);
      }),
    );
  }

  update(shipment :PoShipmentInterface) :Observable<any> {
    this._submittingForm.next(true);

    return this.http.put(`${this.baseUrl}/${shipment.id}`, shipment).pipe(
      map((response :PoShipmentInterface) => {
        const shipments = this.getShipments();

        const updatedIndex = shipments.findIndex(
          (shipment) => shipment.id === response.id,
        );

        if (updatedIndex !== -1) {
          shipments.splice(updatedIndex, 1, response);
          this._shipments.next(shipments);
        }

        this._submittingForm.next(false);

        return response;
      }),
      catchError((response) => {
        this._submittingForm.next(false);

        return throwError(response.error);
      }),
    );
  }

  getShipments() :PoShipmentInterface[] {
    return this._shipments.getValue();
  }

  delete(id :number) :Observable<any> {
    this._deleting.next(true);

    return this.http.delete(`${this.baseUrl}/${id}`).pipe(
      map((response :any) => {
        const shipments = this.getShipments();

        const updatedShipmentStatus = shipments.filter(
          (shipmentStatus) => shipmentStatus.id !== id,
        );
        this._shipments.next(updatedShipmentStatus);
        this._deleting.next(false);

        return response;
      }),
      catchError((response) => {
        this._deleting.next(false);

        return throwError(response.error);
      }),
    );
  }

  search(params :{ [param :string] :number | string | string[] }) :Observable<any> {
    return this.http.get(`${this.baseUrl}/search`, { params });
  }
}
